/**
 * serviceItems - Regras puras dos serviços do Negócio na O.S. (BR-OS-046..049).
 *
 * Sem sessão nem React: reaproveitadas pelo desktop e pelo portal do prestador
 * e cobertas por testes. O servidor revalida tudo (RPCs `_sos_*` e gatilho).
 *
 * RELEASE: 2026-10-18 v1.1.0 - BR-OS-054: origem 'manual', orientação do operador e regras de edição.
 * RELEASE: 2026-10-17 v1.0.0 - Versão inicial.
 */

export type ServiceExecutionStatus = 'pending' | 'done' | 'not_done';
export type ServiceApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface ServiceOrderServiceItem {
  id: string;
  product_id: string | null;
  product_name: string;
  quantity: number;
  origin: 'deal' | 'field' | 'manual';
  execution_status: ServiceExecutionStatus;
  notes: string | null;
  not_done_reason: string | null;
  approval_status: ServiceApprovalStatus | null;
  approval_reason: string | null;
  reviewed_at: string | null;
  removed_from_deal: boolean;
  created_at: string;
  /** BR-OS-054: orientação do operador (somente leitura para o prestador) */
  operator_notes?: string | null;
  locked_by_operator?: boolean;
}

export interface ServiceOrderServicesData {
  deal: { id: string; code: string; title: string } | null;
  deal_changed?: boolean;
  can_refresh?: boolean;
  can_answer: boolean;
  can_manage?: boolean;
  can_edit_notes?: boolean;
  items: ServiceOrderServiceItem[];
}

/** Resposta do prestador para um item. */
export interface ServiceAnswer {
  id: string;
  done: boolean | null;
  notes: string;
  reason: string;
}

export const SERVICE_EXECUTION_LABEL: Record<ServiceExecutionStatus, string> = {
  pending: 'Pendente', done: 'Realizado', not_done: 'Não realizado',
};
export const SERVICE_APPROVAL_LABEL: Record<ServiceApprovalStatus, string> = {
  pending: 'Aguardando análise', approved: 'Aprovado', rejected: 'Recusado',
};

/** Itens que precisam de Sim/Não para concluir: do negócio vigentes e os incluídos pelo operador (BR-OS-047/048/054). */
export function servicesRequiringAnswer(items: ServiceOrderServiceItem[]): ServiceOrderServiceItem[] {
  return items.filter((i) => (i.origin === 'deal' || i.origin === 'manual') && !i.removed_from_deal);
}

/** A O.S. pode concluir quando nenhum serviço do negócio está pendente; adicionais não bloqueiam. */
export function canConcludeWithServices(items: ServiceOrderServiceItem[]): boolean {
  return servicesRequiringAnswer(items).every((i) => i.execution_status !== 'pending');
}

/** Valida as respostas antes de enviar; devolve a mensagem de erro ou null. */
export function validateServiceAnswers(answers: ServiceAnswer[]): string | null {
  if (answers.some((a) => a.done === null)) return 'Informe Sim ou Não em todos os serviços';
  if (answers.some((a) => a.done === false && !a.reason.trim())) return 'Informe o motivo dos serviços não realizados';
  if (answers.some((a) => a.reason.length > 500 || a.notes.length > 500)) return 'Texto acima de 500 caracteres';
  return null;
}

/** Monta as respostas iniciais a partir do que já foi gravado. */
export function initialServiceAnswers(items: ServiceOrderServiceItem[]): ServiceAnswer[] {
  return items.map((i) => ({
    id: i.id,
    done: i.execution_status === 'pending' ? null : i.execution_status === 'done',
    notes: i.notes ?? '',
    reason: i.not_done_reason ?? '',
  }));
}

/** Conteúdo enviado ao servidor (motivo só quando "Não"). */
export function toServiceResultsPayload(answers: ServiceAnswer[]) {
  return answers.map((a) => ({ id: a.id, done: a.done, notes: a.notes.trim() || null, reason: a.done ? null : a.reason.trim() }));
}

/**
 * BR-OS-053 (v1.1.0): quem pode adicionar serviço de campo em cada status.
 * Prestador (app) só Em andamento; gestores (sistema) também Em correção.
 * O servidor (`_sos_add_field` v1.1.0) aplica a mesma regra.
 */
export const PROVIDER_FIELD_ADD_STATUSES = ['in_progress'] as const;
export const MANAGER_FIELD_ADD_STATUSES = ['in_progress', 'in_correction'] as const;
export function canAddFieldService(status: string | null | undefined, actor: 'provider' | 'manager'): boolean {
  const lista: readonly string[] = actor === 'provider' ? PROVIDER_FIELD_ADD_STATUSES : MANAGER_FIELD_ADD_STATUSES;
  return !!status && lista.includes(status);
}

/** Visual da situação de um serviço (bolinha ✓/✗/pendente) — usado no app do prestador. */
export type ServiceVisualTone = 'success' | 'danger' | 'pending' | 'warning';
export function serviceStatusVisual(i: Pick<ServiceOrderServiceItem, 'origin' | 'execution_status' | 'approval_status'>): { tone: ServiceVisualTone; label: string } {
  if (i.execution_status === 'done') return { tone: 'success', label: SERVICE_EXECUTION_LABEL.done };
  if (i.execution_status === 'not_done') return { tone: 'danger', label: SERVICE_EXECUTION_LABEL.not_done };
  if (i.origin === 'field' && i.approval_status) {
    if (i.approval_status === 'approved') return { tone: 'success', label: SERVICE_APPROVAL_LABEL.approved };
    if (i.approval_status === 'rejected') return { tone: 'danger', label: SERVICE_APPROVAL_LABEL.rejected };
    return { tone: 'warning', label: SERVICE_APPROVAL_LABEL.pending };
  }
  return { tone: 'pending', label: SERVICE_EXECUTION_LABEL.pending };
}

/**
 * BR-OS-054: status em que o operador (sistema) adiciona/remove itens e altera quantidade.
 * Mesma lista de `_sos_list.can_manage` e `manage_service_order_services`.
 */
export const OPERATOR_MANAGE_STATUSES = ['open', 'awaiting_shipment', 'shipment_in_transit', 'shipment_delivered', 'scheduled', 'to_reschedule', 'in_correction'] as const;
export function canManageServiceItems(status: string | null | undefined): boolean {
  return !!status && (OPERATOR_MANAGE_STATUSES as readonly string[]).includes(status);
}
/** Quantidade/remoção só em item ainda não respondido pelo prestador. */
export function canEditItemQuantity(i: Pick<ServiceOrderServiceItem, 'execution_status'>, canManage: boolean): boolean {
  return canManage && i.execution_status === 'pending';
}
